/* Monsuri meganav v4-luxe — production runtime
   ─────────────────────────────────────────────────────────────────────────
   Vanilla, defer-loaded, ≤ 4 KB gz target.

   Behavior contract (matches /tmp/v4-design/v4-luxe/{components.css,panels.js}):
     • Desktop hover: 120ms open delay, 300ms close delay, 16px diagonal-tolerance
       bridge between trigger and panel.
     • Keyboard: Arrow keys cycle triggers; ArrowDown enters open panel;
       Esc closes + returns focus.
     • Touch / no-hover: single-tap on tier-1 trigger opens the mobile drawer
       directly (the desktop panel is bypassed entirely).
     • Lazy hydration: hidden source panels in .mega-menu-container clone into
       the visible nav-desktop tier-2 slots on first DOMContentLoaded
       (hydratePanels). Optional <template> tags also supported.
     • Analytics: every clickable element delegates to nav_click on dataLayer.

   This file pairs with:
     • snippets/mega-menu.liquid (emits the panel into .mega-menu-container)
     • snippets/navigation-desktop.liquid (tier-1 ARIA + data-mn-trigger)
     • snippets/mega-menu-mobile-drawer.liquid (m-drawer mount in header)
     • assets/styles.css.liquid (.mn__panel + .mn-host + .m-* classes)
   ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var OPEN_DELAY_MS = 120;
  var CLOSE_DELAY_MS = 300;
  var BRIDGE_PX = 16;

  var triggers = [];
  var panels = new Map(); // handle → { trigger, target, template, hydrated }
  var openTimer = null;
  var closeTimer = null;
  var current = null;
  var lastMouse = { x: 0, y: 0 };
  var hoverNoneMQ = window.matchMedia('(hover: none)');
  // CRITICAL: keep this in sync with CSS @media (max-width: 1100px)
  // breakpoint in styles.css.liquid where panel grids collapse to 1 column.
  // Mismatch causes desktop hover code to run on tablets where the layout
  // has already collapsed.
  var mobileMQ = window.matchMedia('(max-width: 1100px)');

  /* ─────────────────────────────────────────────────────────────────────
     Init
     ───────────────────────────────────────────────────────────────────── */
  function init() {
    if (window.__monsuriMeganavInitialised) return;
    window.__monsuriMeganavInitialised = true;
    hydratePanels();
    bindDesktop();
    bindMobile();
    bindAnalytics();
    bindGlobal();
  }

  /* ─────────────────────────────────────────────────────────────────────
     Hydrate v4 panels into nav-desktop tier-2 containers
     ─────────────────────────────────────────────────────────────────────
     The 6 mega-menu sections render their v4 panels inside
     <div class="mega-menu-container"> at the bottom of the page (which is
     CSS display:none). The visible nav lives in <nav class="nav-desktop">
     where each tier-1 <li> has data-meganav-desktop-target="{handle}".
     This function finds each v4 panel + matching tier-1 li, and replaces
     the li's tier-2 contents (which would otherwise be the legacy
     navigation-widemenu / dropdown-list render) with the v4 panel content.

     Runs once on DOM ready. Idempotent (sets data-mn-hydrated="1" so
     re-running on resize doesn't double-clone).
     ───────────────────────────────────────────────────────────────────── */
  function hydratePanels() {
    var sourcePanels = document.querySelectorAll('[data-meganav-desktop][data-mn-layout]');
    if (sourcePanels.length === 0) return;
    sourcePanels.forEach(function (panel) {
      var handle = panel.getAttribute('data-meganav-handle');
      var layout = panel.getAttribute('data-mn-layout');
      if (!handle) return;
      // Find the matching tier-1 li in the visible nav
      var targetLi = document.querySelector('[data-meganav-desktop-target="' + cssEscape(handle) + '"]');
      if (!targetLi) return;
      if (targetLi.getAttribute('data-mn-hydrated') === '1') return;
      // Find the tier-2 container inside the li (the dropdown / wide panel)
      var tier2 = targetLi.querySelector('.nav-desktop__tier-2');
      if (!tier2) return;

      try {
        if (layout === 'none') {
          // Layout=none path: the merchant chose NO v4 panel for this
          // parent. Keep the legacy widemenu/linklist content rendered
          // by navigation-desktop.liquid, but switch the tier-2 CSS
          // class from --full-width-menu to --dropdown so a parent
          // with 2-3 tier-2 links renders as a compact vertical list
          // instead of spreading horizontally across the viewport.
          //
          // Also swap the parent li's modifier class:
          //   --widemenu-parent  → no `position: relative` → tier-2's
          //                        `position: absolute` resolves
          //                        against the viewport (renders at
          //                        x=0, the left edge of screen).
          //   --dropdown-parent  → `position: relative` → tier-2
          //                        positions correctly below the
          //                        parent link.
          // Without this class swap the dropdown rendered in the
          // wrong x-position even after the tier-2 class change.
          tier2.classList.remove('nav-desktop__tier-2--full-width-menu');
          tier2.classList.add('nav-desktop__tier-2--dropdown');
          tier2.removeAttribute('data-nav-desktop-full-width-menu');
          targetLi.classList.remove('nav-desktop__tier-1-item--widemenu-parent');
          targetLi.classList.add('nav-desktop__tier-1-item--dropdown-parent');
          targetLi.setAttribute('data-mn-hydrated', '1');
          tier2.setAttribute('data-mn-panel-target', handle);
        } else {
          // Layout=gifts/editorial/mindful/sanctuary path: clone the v4
          // panel into the tier-2 slot. Hide the pre-existing widemenu /
          // dropdown-list content (preserved for no-JS fallback). Tag
          // the tier-2 so openPanel can find it.
          Array.prototype.forEach.call(tier2.children, function (child) {
            if (!child.classList.contains('mn__panel') && !child.hasAttribute('data-mn-cloned')) {
              child.style.display = 'none';
            }
          });
          var clone = panel.cloneNode(true);
          clone.setAttribute('data-mn-cloned', '1');
          clone.removeAttribute('data-meganav-desktop');
          tier2.appendChild(clone);
          targetLi.setAttribute('data-mn-hydrated', '1');
          tier2.setAttribute('data-mn-panel-target', handle);
        }
      } catch (err) {
        if (window.console && window.console.warn) {
          console.warn('meganav: hydrate failed for handle=' + handle, err);
        }
      }
    });

    // Fallback pass: tier-1 parents that have NO mega-menu section
    // configured at all (no `data-meganav-desktop` source emitted) still
    // render their dropdown via the legacy widemenu path with
    // `--full-width-menu`. That makes 2-3 simple tier-2 links spread
    // across the viewport. Treat any un-hydrated tier-1 with widemenu-
    // parent class the same way as layout=none: swap to compact
    // dropdown. The merchant doesn't have to author an empty
    // mega-menu section just to get the compact look.
    var unconfigured = document.querySelectorAll(
      '.nav-desktop__tier-1-item--widemenu-parent[data-meganav-desktop-target]:not([data-mn-hydrated])'
    );
    Array.prototype.forEach.call(unconfigured, function (li) {
      var tier2 = li.querySelector('.nav-desktop__tier-2');
      if (!tier2) return;
      try {
        tier2.classList.remove('nav-desktop__tier-2--full-width-menu');
        tier2.classList.add('nav-desktop__tier-2--dropdown');
        tier2.removeAttribute('data-nav-desktop-full-width-menu');
        li.classList.remove('nav-desktop__tier-1-item--widemenu-parent');
        li.classList.add('nav-desktop__tier-1-item--dropdown-parent');
        li.setAttribute('data-mn-hydrated', '1');
        var handle = li.getAttribute('data-meganav-desktop-target');
        if (handle) tier2.setAttribute('data-mn-panel-target', handle);
      } catch (err) {
        if (window.console && window.console.warn) {
          console.warn('meganav: fallback hydrate failed', err);
        }
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     Desktop: hover-intent + bridge + keyboard
     ───────────────────────────────────────────────────────────────────── */
  function bindDesktop() {
    triggers = Array.prototype.slice.call(
      document.querySelectorAll('[data-mn-trigger]')
    );
    triggers.forEach(function (trigger) {
      var handle = trigger.getAttribute('data-mn-trigger');
      if (!handle) return;
      var target = document.querySelector('[data-mn-panel-target="' + cssEscape(handle) + '"]');
      var template = document.querySelector('template[data-mn-panel-template="' + cssEscape(handle) + '"]');
      panels.set(handle, { trigger: trigger, target: target, template: template, hydrated: false });

      trigger.addEventListener('mouseenter', function () {
        if (hoverNoneMQ.matches) return; // touch devices skip hover
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(function () { openPanel(handle, 'hover'); }, OPEN_DELAY_MS);
      });
      trigger.addEventListener('mouseleave', scheduleClose);
      trigger.addEventListener('focus', function () { openPanel(handle, 'keyboard'); });
      trigger.addEventListener('keydown', function (e) { onTriggerKeydown(e, handle); });
      // Suppress the browser's native <details>/<summary> toggle BEFORE click
      // fires — preventDefault on click is reliable for following anchor href,
      // but the <details> toggle is a native action on summary mousedown that
      // some browsers (notably older Safari) fire pre-click. Defensive: catch
      // mousedown and pointerdown too.
      var suppressNativeToggle = function (e) {
        // Only on the summary itself, only on primary button / no-modifier
        if (e.button !== 0 && e.pointerType !== 'mouse' && e.pointerType !== 'touch' && e.pointerType !== undefined) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (trigger.tagName === 'SUMMARY') e.preventDefault();
      };
      trigger.addEventListener('mousedown', suppressNativeToggle);
      trigger.addEventListener('pointerdown', suppressNativeToggle);

      trigger.addEventListener('click', function (e) {
        if (hoverNoneMQ.matches || mobileMQ.matches) {
          e.preventDefault();
          openMobileDrawer(handle);
          return;
        }
        // On desktop: toggle (don't let <details> auto-open without JS contract)
        var p = panels.get(handle);
        if (p && p.target && !p.target.hidden) {
          closeAll('click');
        } else {
          openPanel(handle, 'click');
        }
        e.preventDefault();
      });
    });

    // Track mouse for bridge math
    document.addEventListener('mousemove', function (e) {
      lastMouse = { x: e.clientX, y: e.clientY };
    }, { passive: true });

    // Cancel close when cursor enters a panel
    panels.forEach(function (p) {
      if (!p.target) return;
      p.target.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      p.target.addEventListener('mouseleave', scheduleClose);
    });
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      // Bridge tolerance: cursor heading toward panel? keep it open
      if (current) {
        var p = panels.get(current);
        if (p && p.target) {
          var r = p.target.getBoundingClientRect();
          var withinBridge = lastMouse.y >= (r.top - BRIDGE_PX)
            && lastMouse.x >= (r.left - BRIDGE_PX)
            && lastMouse.x <= (r.right + BRIDGE_PX);
          if (withinBridge) return;
        }
      }
      closeAll('hover');
    }, CLOSE_DELAY_MS);
  }

  function openPanel(handle, openMethod) {
    var p = panels.get(handle);
    if (!p || !p.target) return;
    if (current && current !== handle) closeAll(openMethod);

    // Optional lazy hydration: clone <template> on first open if present.
    // When MeganavManager has already populated the target via its own
    // clone path, p.template is null and we skip this step.
    if (!p.hydrated && p.template && p.template.content) {
      try {
        p.target.appendChild(p.template.content.cloneNode(true));
        p.hydrated = true;
        // Re-bind panel mouseenter/leave on the just-hydrated subtree
        p.target.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
        p.target.addEventListener('mouseleave', scheduleClose);
      } catch (err) {
        if (window.console && window.console.warn) console.warn('meganav: hydrate failed', err);
      }
    }

    p.target.hidden = false;
    p.target.setAttribute('aria-hidden', 'false');
    p.trigger.setAttribute('aria-expanded', 'true');
    // Sync the legacy <details> wrapper (no-JS fallback) if present
    var det = p.trigger.closest && p.trigger.closest('details');
    if (det) det.open = true;
    current = handle;
    pushAnalytics({
      component: 'tier1',
      tier1: handle,
      open_method: openMethod || 'hover'
    });
  }

  function closeAll(/* method */) {
    panels.forEach(function (p) {
      if (!p.target) return;
      p.target.hidden = true;
      p.target.setAttribute('aria-hidden', 'true');
      if (p.trigger) p.trigger.setAttribute('aria-expanded', 'false');
      var det = p.trigger && p.trigger.closest && p.trigger.closest('details');
      if (det) det.open = false;
    });
    current = null;
  }

  function onTriggerKeydown(e, handle) {
    var key = e.key;
    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      var p = panels.get(handle);
      var open = p && p.target && !p.target.hidden;
      if (open) closeAll('keyboard'); else openPanel(handle, 'keyboard');
      return;
    }
    if (key === 'Escape') { e.preventDefault(); closeAll('keyboard'); focusTrigger(handle); return; }
    if (key === 'ArrowRight') { e.preventDefault(); cycleTrigger(handle, 1); return; }
    if (key === 'ArrowLeft')  { e.preventDefault(); cycleTrigger(handle, -1); return; }
    if (key === 'ArrowDown') {
      e.preventDefault();
      openPanel(handle, 'keyboard');
      var p = panels.get(handle);
      if (p && p.target) {
        var firstLink = p.target.querySelector('a, button');
        if (firstLink && firstLink.focus) firstLink.focus();
      }
    }
  }

  function focusTrigger(handle) {
    var p = panels.get(handle);
    if (p && p.trigger && p.trigger.focus) p.trigger.focus();
  }

  function cycleTrigger(currentHandle, dir) {
    var idx = triggers.findIndex(function (t) {
      return t.getAttribute('data-mn-trigger') === currentHandle;
    });
    if (idx < 0) return;
    var next = (idx + dir + triggers.length) % triggers.length;
    triggers[next].focus();
  }

  /* ─────────────────────────────────────────────────────────────────────
     Mobile drawer (v4.1 vertical accordion)
     ─────────────────────────────────────────────────────────────────────
     The drawer body is a list of <details data-mn-tier1="{handle}"> elements.
     Each summary-click expands ONE tier-1 inline and collapses the others.
     Pane content is lazy-cloned from <template data-mn-mobile-pane="{handle}">
     into the matching <details> body on first expand.

     Tier-1s without a matching mega-menu pane (Sale, Blog, Contact, etc.)
     fall through to data-mn-mobile-href navigation when their summary fires.
     ───────────────────────────────────────────────────────────────────── */
  var drawer = null;
  var drawerTier1s = new Map(); // handle → { details, summary, paneSlot, template, hydrated }
  var drawerOpenerEl = null;    // element that opened the drawer — focus returns here on close
  var drawerKeydownHandler = null;

  function bindMobile() {
    drawer = document.querySelector('[data-mn-mobile-drawer]');
    if (!drawer) return;

    // Pane templates are rendered server-side per mega-menu section. They
    // live in <div class="mega-menu-container"> at the page footer — NOT
    // inside the drawer. Search the whole document.
    var paneTpls = new Map();
    document.querySelectorAll('template[data-mn-mobile-pane]').forEach(function (tpl) {
      var handle = tpl.getAttribute('data-mn-mobile-pane');
      if (handle) paneTpls.set(handle, tpl);
    });

    // Wire each <details data-mn-tier1>: lazy-clone the matching pane template
    // on first expand, collapse siblings, drive aria-expanded on the summary.
    drawer.querySelectorAll('details[data-mn-tier1]').forEach(function (details) {
      var handle = details.getAttribute('data-mn-tier1');
      var summary = details.querySelector('summary');
      var paneSlot = details.querySelector('[data-mn-tier1-pane]');
      if (!handle || !summary || !paneSlot) return;
      var template = paneTpls.get(handle) || null;

      drawerTier1s.set(handle, {
        details: details, summary: summary, paneSlot: paneSlot,
        template: template, hydrated: false
      });

      summary.setAttribute('aria-expanded', 'false');

      // Suppress the native <details> toggle so we control open/close
      // ourselves (needed for "one open at a time" + URL-fallback path).
      summary.addEventListener('click', function (e) {
        e.preventDefault();

        var fallbackHref = details.getAttribute('data-mn-mobile-href');

        // Tier-1 with no matching pane → navigate directly (Sale, Blog, …).
        if (!template) {
          if (fallbackHref && fallbackHref !== '#') {
            window.location.href = fallbackHref;
          }
          return;
        }

        var willOpen = !details.open;

        // Collapse every other tier-1 first (one open at a time).
        if (willOpen) {
          drawerTier1s.forEach(function (t, h) {
            if (h !== handle && t.details.open) {
              t.details.open = false;
              if (t.summary) t.summary.setAttribute('aria-expanded', 'false');
            }
          });
        }

        // Lazy-clone the pane template on first expand (subsequent opens
        // just toggle visibility — no re-clone).
        if (willOpen && !drawerTier1s.get(handle).hydrated) {
          try {
            paneSlot.appendChild(template.content.cloneNode(true));
            drawerTier1s.get(handle).hydrated = true;
          } catch (err) {
            if (window.console && window.console.warn) {
              console.warn('meganav: mobile hydrate failed for handle=' + handle, err);
            }
          }
        }

        details.open = willOpen;
        summary.setAttribute('aria-expanded', String(willOpen));

        if (willOpen) {
          pushAnalytics({ component: 'mobile_tier1', tier1: handle, open_method: 'click' });
        }
      });
    });

    // Open / close triggers in header (used by openMobileDrawer / desktop click
    // fallback on touch devices). The legacy <details data-mobile-menu> in
    // sections/header.liquid is the visual hamburger — we still respect any
    // [data-mn-mobile-open] / [data-mn-mobile-close] hooks that custom theme
    // chrome may add later.
    var openBtns = document.querySelectorAll('[data-mn-mobile-open]');
    openBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openMobileDrawer(btn.getAttribute('data-mn-mobile-open') || null);
      });
    });

    var closeBtns = drawer.querySelectorAll('[data-mn-mobile-close]');
    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMobileDrawer();
      });
    });

    // Esc closes the drawer (only when standalone open — not when the legacy
    // <details data-mobile-menu> hamburger is in charge).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
        closeMobileDrawer();
      }
    });

    // Phase C — sticky tier-1 chip rail. See bindStickyChipRail() below.
    bindStickyChipRail();
  }

  /* ─────────────────────────────────────────────────────────────────────
     Phase C — sticky in-pane tier-1 chip rail
     ─────────────────────────────────────────────────────────────────────
     When a tier-1 is expanded AND its summary scrolls offscreen, a
     horizontal pill rail pins to the top of the drawer with one chip per
     tier-1. The active chip (matching the open <details>) gets aria-current
     and a sage tint. Tapping a chip routes through the existing summary
     click handler (so the same one-open-at-a-time + lazy-clone contract
     applies), then smooth-scrolls the new summary into view.

     IntersectionObserver watches a single sentinel element placed above
     the accordion — cheaper than observing every summary, and the trigger
     point is the same (sentinel offscreen ↔ accordion top scrolled past).

     The rail is `aria-hidden="true"` until pinned, so screen readers don't
     see a duplicate set of tier-1 navigators sitting in the document flow
     when the user hasn't scrolled into the pane yet.
     ───────────────────────────────────────────────────────────────────── */
  function bindStickyChipRail() {
    if (!drawer) return;
    var sentinel = drawer.querySelector('[data-mn-sticky-sentinel]');
    var rail     = drawer.querySelector('[data-mn-sticky-rail]');
    if (!sentinel || !rail) return;

    var chips = Array.prototype.slice.call(rail.querySelectorAll('[data-mn-sticky-chip]'));

    // Reflect the currently-open tier-1 on the chip rail (aria-current +
    // .is-active). Called whenever any <details data-mn-tier1> toggles.
    function syncActiveChip() {
      var openHandle = null;
      drawerTier1s.forEach(function (t, h) {
        if (t.details.open) openHandle = h;
      });
      var hasOpen = !!openHandle;
      drawer.classList.toggle('m-acc-tier1--has-open', hasOpen);
      var activeChip = null;
      chips.forEach(function (chip) {
        var match = chip.getAttribute('data-mn-sticky-chip') === openHandle;
        chip.classList.toggle('is-active', match);
        if (match) {
          chip.setAttribute('aria-current', 'true');
          activeChip = chip;
        } else {
          chip.removeAttribute('aria-current');
        }
      });
      // When the rail is pinned, scroll the active chip into view inside
      // the horizontal track so the user sees their current category.
      if (rail.classList.contains('is-pinned') && activeChip && activeChip.scrollIntoView) {
        try {
          activeChip.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        } catch (e) { /* old Safari ignores opts — no-op */ }
      }
    }

    // Pin/unpin via a throttled scroll listener.
    //
    // The legacy `#header` is `position: fixed` AND has its own
    // `overflow-y: scroll` — the drawer scrolls INSIDE the fixed header,
    // not on the page. So `window.scroll` never fires for this drawer; we
    // have to listen on the actual scroll container, which we resolve by
    // walking up the ancestor chain looking for an overflow:auto|scroll.
    // Falls back to window if no scrolling ancestor is found.
    function findScrollParent(el) {
      var p = el && el.parentElement;
      while (p && p !== document.documentElement) {
        var cs = getComputedStyle(p);
        if (/(auto|scroll)/.test(cs.overflowY + ' ' + cs.overflow)) return p;
        p = p.parentElement;
      }
      return window;
    }
    var scrollParent = findScrollParent(sentinel);
    // The sentinel.top reading is relative to the viewport, but our pin
    // condition is "sentinel scrolled past the top of the scroll
    // container." When the scroll container is the window, those are the
    // same. When it's a fixed-positioned element with internal scrolling,
    // we need to compare sentinel.top to the scroll container's top.
    var rafScheduled = false;
    var lastPinned = false;
    // The aria-label lives on the static markup as data-mn-sticky-rail-label
    // (locale-resolved server-side). We move it on/off the live `aria-label`
    // attribute alongside the aria-hidden toggle so the rail never appears
    // to AT as both labelled AND hidden — see the comment in
    // mega-menu-mobile-drawer.liquid for the contradiction we're avoiding.
    var railLabel = rail.getAttribute('data-mn-sticky-rail-label') || '';
    function evaluatePinned() {
      rafScheduled = false;
      // Only meaningful when at least one tier-1 is open. Otherwise the
      // rail has nothing to highlight and we don't want to surface chrome
      // for an idle state.
      var hasOpen = false;
      drawerTier1s.forEach(function (t) { if (t.details.open) hasOpen = true; });
      var sentinelTop = sentinel.getBoundingClientRect().top;
      var containerTop = (scrollParent === window) ? 0 : scrollParent.getBoundingClientRect().top;
      var pinned = hasOpen && sentinelTop < containerTop;
      if (pinned !== lastPinned) {
        rail.classList.toggle('is-pinned', pinned);
        rail.setAttribute('aria-hidden', String(!pinned));
        if (pinned && railLabel) {
          rail.setAttribute('aria-label', railLabel);
        } else {
          rail.removeAttribute('aria-label');
        }
        lastPinned = pinned;
        if (pinned) syncActiveChip();
      }
    }
    function onScrollOrResize() {
      if (rafScheduled) return;
      rafScheduled = true;
      window.requestAnimationFrame(evaluatePinned);
    }
    scrollParent.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    // Wire each chip → route to the matching summary's click. The existing
    // summary handler manages collapse-siblings + lazy-clone; we just need
    // to scroll the user back to the new tier-1 after the swap.
    chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        var handle = chip.getAttribute('data-mn-sticky-chip');
        var t = drawerTier1s.get(handle);
        if (!t) return;
        var alreadyOpen = t.details.open;
        if (!alreadyOpen) t.summary.click();
        // Scroll the user to the newly-active tier-1's summary so they
        // land on the new pane, not stuck in the previous pane's scroll
        // position. Defer one tick so the lazy-clone has run.
        setTimeout(function () {
          if (t.summary && t.summary.scrollIntoView) {
            try { t.summary.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
            catch (err) { t.summary.scrollIntoView(); }
          }
          syncActiveChip();
        }, alreadyOpen ? 0 : 50);
        pushAnalytics({ component: 'mobile_chip', tier1: handle, open_method: 'click' });
      });
    });

    // Listen for any tier-1 toggle so the chip rail's active state stays
    // in sync (e.g. the user taps a summary directly while the rail is
    // pinned, or collapses everything via Esc). The toggle handler also
    // re-evaluates pin state — opening a tier-1 while already scrolled past
    // the sentinel should pin the rail immediately, and collapsing the
    // last open tier-1 should unpin it.
    drawerTier1s.forEach(function (t) {
      if (!t.details) return;
      t.details.addEventListener('toggle', function () {
        syncActiveChip();
        onScrollOrResize();
      });
    });

    // Initial state
    syncActiveChip();
    evaluatePinned();
  }

  function getFocusable(root) {
    if (!root) return [];
    // bbox check instead of offsetParent — iOS Safari returns null for
    // offsetParent inside position:fixed ancestors (WebKit bug 106162). Our
    // drawer mounts inside a sticky/fixed header, so offsetParent would
    // collapse the focus trap to zero items on every iPhone.
    return Array.prototype.slice.call(
      root.querySelectorAll(
        'a[href], button:not([disabled]), summary, input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      if (el.hidden) return false;
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
  }

  function openMobileDrawer(handle) {
    if (!drawer) return;
    drawerOpenerEl = document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('is-mn-drawer-open');

    // If a tier-1 handle was passed (e.g., desktop trigger tapped on mobile),
    // expand it immediately. Otherwise leave all collapsed (preferred — every
    // tier-1 is one tap away, no pre-selected pane biasing the fold).
    // Guard: only fire summary.click() if not already open. The summary
    // listener toggles based on `!details.open`, so a programmatic click on
    // an already-open <details> would collapse it instead of keeping it open.
    if (handle) {
      var t = drawerTier1s.get(handle);
      if (t && t.template && !t.details.open && t.summary && t.summary.click) {
        t.summary.click();
      }
    }

    // Focus the first focusable inside the drawer (after one tick for hydration)
    setTimeout(function () {
      var first = getFocusable(drawer)[0];
      if (first && first.focus) first.focus();
    }, 0);

    // Focus trap: Tab/Shift+Tab cycles within the drawer
    drawerKeydownHandler = function (e) {
      if (e.key !== 'Tab') return;
      var focusables = getFocusable(drawer);
      if (focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', drawerKeydownHandler);

    pushAnalytics({ component: 'mobile_drawer_open', tier1: handle || '', open_method: 'click' });
  }

  function closeMobileDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('hidden', '');
    document.body.style.overflow = '';
    document.body.classList.remove('is-mn-drawer-open');
    if (drawerKeydownHandler) {
      document.removeEventListener('keydown', drawerKeydownHandler);
      drawerKeydownHandler = null;
    }
    // Return focus to the element that opened the drawer (a11y contract)
    if (drawerOpenerEl && drawerOpenerEl.focus) drawerOpenerEl.focus();
    drawerOpenerEl = null;
  }

  /* ─────────────────────────────────────────────────────────────────────
     Analytics — delegated nav_click on every clickable element
     ───────────────────────────────────────────────────────────────────── */
  function bindAnalytics() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('[data-mn-track]');
      if (!el) return;
      var component = el.getAttribute('data-mn-track');
      // Walk up to find the panel handle
      var panel = el.closest('[data-meganav-handle]') || el.closest('[data-mn-panel-target]') || el.closest('[data-mn-tier1]');
      var tier1 = panel ? (panel.getAttribute('data-meganav-handle') || panel.getAttribute('data-mn-panel-target') || panel.getAttribute('data-mn-tier1') || '') : '';
      var slot = el.getAttribute('data-mn-mood') || el.getAttribute('data-mn-product-handle') || el.getAttribute('data-mn-rail-slot') || el.getAttribute('data-mn-story-slot') || '';
      var href = el.getAttribute('href') || '';
      var siblings = el.parentElement ? el.parentElement.children : [];
      var pos = 0;
      for (var i = 0; i < siblings.length; i++) { if (siblings[i] === el) { pos = i + 1; break; } }
      pushAnalytics({
        component: component,
        tier1: tier1,
        slot: slot,
        href: href,
        position: pos,
        device: mobileMQ.matches ? 'mobile' : 'desktop',
        open_method: 'click'
      });
    }, { passive: true });
  }

  function pushAnalytics(payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var record = { event: 'nav_click', nav_version: 'v4' };
      for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) record[k] = payload[k];
      // Sensible defaults
      if (!record.device) record.device = mobileMQ.matches ? 'mobile' : 'desktop';
      window.dataLayer.push(record);
    } catch (e) { /* analytics never throws */ }
  }

  /* ─────────────────────────────────────────────────────────────────────
     Global handlers — Esc, click-outside
     ───────────────────────────────────────────────────────────────────── */
  function bindGlobal() {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) {
        e.preventDefault();
        var was = current;
        closeAll('keyboard');
        focusTrigger(was);
      }
    });
    document.addEventListener('click', function (e) {
      if (!current) return;
      var trigger = e.target.closest('[data-mn-trigger]');
      var panel = e.target.closest('[data-mn-panel-target]');
      if (!trigger && !panel) closeAll('click');
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     Tiny utility — CSS.escape polyfill for older Safari
     ───────────────────────────────────────────────────────────────────── */
  function cssEscape(str) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(str);
    return String(str).replace(/[^\w-]/g, function (c) { return '\\' + c; });
  }

  /* ─────────────────────────────────────────────────────────────────────
     Boot
     ───────────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
